import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { getColorByUserId } from 'constants/presence';
import { collapseConnectionsByUser } from 'utils/presence';

const selectPresenceState = state => state.presence;

export const selectPresenceUsers = createSelector(selectPresenceState, ({ users }) =>
  Object.values(users),
);

export const selectCurrentConnectionId = createSelector(
  selectPresenceState,
  ({ currentConnectionId }) => currentConnectionId,
);

// The store is one slice shared by every presence-mounting surface, so a surface must filter to
// the records its own subscription delivered. Relying on the leave/reset instead would surface a
// stale entity's co-viewers whenever an in-flight event lands after the next join.
export const selectPresenceUsersForEntity = memoizeOne(entityRef =>
  createSelector(selectPresenceUsers, users => users.filter(user => user.entityRef === entityRef)),
);

export const selectOtherUsersOnTab = memoizeOne(tab =>
  createSelector(selectPresenceUsers, selectCurrentConnectionId, (users, currentConnectionId) =>
    users.filter(user => user.tab === tab && user.connectionId !== currentConnectionId),
  ),
);

export const selectOtherUsersOnSubtab = memoizeOne(subtab =>
  createSelector(selectPresenceUsers, selectCurrentConnectionId, (users, currentConnectionId) =>
    users.filter(user => user.subtab === subtab && user.connectionId !== currentConnectionId),
  ),
);

export const selectOtherUsersOnEntity = memoizeOne(entityId =>
  createSelector(selectPresenceUsers, selectCurrentConnectionId, (users, currentConnectionId) =>
    users.filter(user => user.entityId === entityId && user.connectionId !== currentConnectionId),
  ),
);

export const selectOtherUsersInSection = memoizeOne(sectionId =>
  createSelector(selectPresenceUsers, selectCurrentConnectionId, (users, currentConnectionId) =>
    users.filter(
      user =>
        user.connectionId !== currentConnectionId &&
        (user.sectionId === sectionId ||
          user.subtab === sectionId ||
          user.entityId === sectionId ||
          user.subsection === sectionId),
    ),
  ),
);

export const selectOtherUsersInSectionWithSubtab = memoizeOne((sectionId, subtab) =>
  createSelector(selectPresenceUsers, selectCurrentConnectionId, (users, currentConnectionId) =>
    users.filter(
      user =>
        user.connectionId !== currentConnectionId &&
        user.entityId === sectionId &&
        user.subtab === subtab,
    ),
  ),
);

export const selectFieldLock = memoizeOne(fieldPath =>
  createSelector(selectPresenceState, ({ fieldLocks }) => fieldLocks[fieldPath]),
);

export const selectIsFieldLocked = memoizeOne(fieldPath =>
  createSelector(selectPresenceState, ({ fieldLocks }) => !!fieldLocks[fieldPath]),
);

export const selectIsFieldLockedByMe = memoizeOne(fieldPath =>
  createSelector(selectPresenceState, ({ fieldLocks, currentConnectionId }) => {
    const lock = fieldLocks[fieldPath];

    return !!lock && lock.connectionId === currentConnectionId;
  }),
);

export const selectAllFieldLocks = createSelector(
  selectPresenceState,
  ({ fieldLocks }) => fieldLocks,
);

export const selectAllRemoteFieldValues = createSelector(
  selectPresenceState,
  ({ remoteFieldValues }) => remoteFieldValues,
);

export const selectRemoteFieldValuesClearReason = createSelector(
  selectPresenceState,
  ({ remoteFieldValuesClearReason }) => remoteFieldValuesClearReason,
);

export const selectLocalEditedFieldPaths = createSelector(
  selectPresenceState,
  ({ localEditedFieldPaths }) => localEditedFieldPaths,
);

export const selectIsPresenceConnected = createSelector(
  selectPresenceState,
  ({ isConnected }) => isConnected,
);

export const selectPresenceNotifications = createSelector(
  selectPresenceState,
  ({ notifications }) => notifications,
);

export const selectFollowingUserId = createSelector(
  selectPresenceState,
  ({ followingUserId }) => followingUserId,
);

export const selectIsFollowing = createSelector(
  selectFollowingUserId,
  followingUserId => !_.isNil(followingUserId),
);

export const selectFollowedUser = createSelector(
  selectPresenceState,
  selectPresenceUsers,
  ({ followingUserId, followingConnectionId, users }, presenceUsers) => {
    if (_.isNil(followingUserId)) {
      return null;
    }

    const followedConnections = presenceUsers.filter(user => user.userId === followingUserId);

    if (!followedConnections.length) {
      return null;
    }

    const heldConnection = !_.isNil(followingConnectionId)
      ? (users[followingConnectionId] ?? null)
      : null;

    if (!_.isNil(heldConnection)) {
      return heldConnection;
    }

    const [representative] = collapseConnectionsByUser(followedConnections);

    return representative;
  },
);

export const selectPresenceUserColorMap = createSelector(selectPresenceState, ({ users }) =>
  Object.values(users).reduce((acc, user) => {
    acc[user.connectionId] = getColorByUserId(user.userId);

    return acc;
  }, {}),
);

export const selectPresenceUserIdColorMap = createSelector(selectPresenceState, ({ users }) =>
  Object.values(users).reduce((acc, user) => {
    if (_.isNil(acc[user.userId])) {
      acc[user.userId] = getColorByUserId(user.userId);
    }

    return acc;
  }, {}),
);
